import { useMemo } from 'react';

import { uniqBy } from 'lodash';
import type { AlineUserMentionData, MentionEntity } from 'src/components';
import { Apollo } from 'src/gql/generated';
import type { UserSubset } from 'src/types';

import { useGetDocument } from './document';
import type { useGetStorablePermissions } from './storable-permissions';

export function useGetMentionableUsers(
  mentionEntity: MentionEntity,
  options: {
    permissions: ReturnType<typeof useGetStorablePermissions>['permissions'];
  }
): {
  loading: boolean;
  mentionableUsers: UserSubset[];
} {
  const { permissions } = options;
  const variablesObj = createGetAccountsVariables(mentionEntity);
  const { document } = useGetDocument(
    mentionEntity.__typename === 'Document' ? mentionEntity.id : null
  );
  const { data: accountsData, loading } = Apollo.useGetAccountsByQuery({
    ...variablesObj,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !mentionEntity,
  } as Parameters<typeof Apollo.useGetAccountsByQuery>[0]);
  const mentionableUsers = useMemo((): AlineUserMentionData['user'][] => {
    const usersInAccount = accountsData?.accounts.users ?? [];
    const permissionUsers = permissions.flatMap(p => {
      return p.account.__typename === 'User' ? [p.account] : [];
    });
    const nonUniqMentionableUsers: AlineUserMentionData['user'][] = [
      ...usersInAccount,
      ...permissionUsers,
    ];
    if (document?.owner) nonUniqMentionableUsers.push(document.owner);

    return uniqBy(nonUniqMentionableUsers, u => u.id).sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  }, [accountsData?.accounts.users, document?.owner, permissions]);

  return { loading, mentionableUsers };
}

function createGetAccountsVariables(
  mentionEntity?: MentionEntity
): Pick<NonNullable<Parameters<typeof Apollo.useGetAccountsByQuery>[0]>, 'variables'> {
  if (!mentionEntity) return { variables: { input: {} } };

  const key: keyof Apollo.AccountsByInput =
    mentionEntity.__typename === 'Document' ? 'documentId' : 'workflowId';
  const input: Apollo.AccountsByInput = { [key]: mentionEntity.id };

  return { variables: { input } };
}
